import useWorksMutation from "@/api/works/useWorksMutation";
import { useModal } from "@/features/modal/ModalStackManager";
import { EquipmentAutoComplete } from "@/features/ui/autoComplete/equipment/equipment-autoComplete";
import { BaseDateInput } from "@/features/ui/dateInput/BaseDateInput";
import { handleErrorResponse } from "@/utils/errorMessage";
import { Button, Flex, Input, Table, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import {
  AuthSignupPost201ResponseEquipment,
  AuthSignupPost201ResponseEquipmentWorksInner,
  AuthSignupPost201ResponseEquipmentWorksInnerTrackingStatusEnum,
  DefaultApiProductionPlansProductionPlanIdPutRequest,
  DefaultApiWorksWorkIdPutRequest,
  ProductionPlansGet200ResponseRowsInnerWorksInner,
  ProductionPlansGet200ResponseRowsInnerWorksInnerTrackingStatusEnum,
  WorksItemGet200ResponseRowsInner,
  WorksItemGet200ResponseRowsInnerTrackingStatusEnum,
  WorksWorkIdDeleteRequest,
} from "@sizlcorp/sizl-api-document/dist/models";
import { IconCalendar } from "@tabler/icons-react";
import customAlert from "../ui/alert/Alert";

type PageKeyType = "equipment" | "plan"; // pageKey의 가능한 값들을 정의합니다.

type PlanWorkRow =
  | ProductionPlansGet200ResponseRowsInnerWorksInner
  | WorksItemGet200ResponseRowsInner;
type EquipmentWorkRow = AuthSignupPost201ResponseEquipmentWorksInner;
type WorkRow = PlanWorkRow | EquipmentWorkRow;

type ItemType<T extends PageKeyType> = T extends "equipment" ? EquipmentWorkRow : PlanWorkRow; // pageKey에 따른 item의 타입을 정의합니다.

type TrackingStatusType =
  | AuthSignupPost201ResponseEquipmentWorksInnerTrackingStatusEnum
  | ProductionPlansGet200ResponseRowsInnerWorksInnerTrackingStatusEnum
  | WorksItemGet200ResponseRowsInnerTrackingStatusEnum;

const isPlanWorkRow = (work?: WorkRow): work is PlanWorkRow =>
  Boolean(work && "productionPlan" in work);

interface WorkUpdateFormProps<T extends PageKeyType> {
  workRowData: ItemType<T>[];
}
export function WorkUpdateForm<T extends PageKeyType>(params: WorkUpdateFormProps<T>): JSX.Element {
  const workDataArray = params.workRowData;

  const isPlan = isPlanWorkRow(workDataArray[0]);
  console.log(workDataArray, "workDataArray");
  const { closeModal } = useModal();
  const { mutate: updateMutate } = useWorksMutation("update");
  const form = useForm<{
    works: Array<
      Partial<
        DefaultApiWorksWorkIdPutRequest & DefaultApiProductionPlansProductionPlanIdPutRequest
      > & {
        equipmentType?: AuthSignupPost201ResponseEquipment["equipmentType"];
        trackingStatus?: TrackingStatusType;
      }
    >;
  }>({
    initialValues: {
      works: workDataArray.map((workData) => {
        const planWorkData = isPlanWorkRow(workData) ? workData : undefined;

        return {
          workId: workData.id,
          worksWorkIdDeleteRequest: {
            routingCode: workData.routingCode,
            equipmentCode: workData.equipmentCode,
            targetQuantity: workData.targetQuantity,
            scheduledAt: planWorkData?.scheduledAt,
            kgPerCount: planWorkData?.kgPerCount,
          },
          productionPlanId: workData.productionPlanId,
          productionPlansProductionPlanIdDeleteRequest: planWorkData?.productionPlan
            ? {
                presetLotName: planWorkData.productionPlan?.presetLotName,
              }
            : {},
          equipmentType: planWorkData?.equipment?.equipmentType,
          trackingStatus: workData.trackingStatus,
        };
      }),
    },
  });

  const onSave = () => {
    form.values.works.forEach((work, index) => {
      const { workId, worksWorkIdDeleteRequest, productionPlanId } = work;

      updateMutate(
        {
          workId: workId as number,
          worksWorkIdDeleteRequest: worksWorkIdDeleteRequest as WorksWorkIdDeleteRequest,
          productionPlanId: productionPlanId as number,
          productionPlansProductionPlanIdDeleteRequest: {
            scheduledAt: worksWorkIdDeleteRequest?.scheduledAt!,
            targetQuantity: worksWorkIdDeleteRequest?.targetQuantity!,
            presetLotName: work.productionPlansProductionPlanIdDeleteRequest?.presetLotName!,
          },
        },
        {
          onSuccess: () => {
            if (index === form.values.works.length - 1) {
              customAlert("작업 지시 수정에 성공했습니다.", "작업 지시 수정 성공", "green");
              form.reset();
              closeModal(true);
            }
          },
          onError: (error) => {
            customAlert(
              handleErrorResponse({ error, errorContext: "작업 지시" }),
              "작업 지시 수정 실패",
              "red",
            );
            closeModal(false);
          },
        },
      );
    });
  };

  return (
    <>
      <Table>
        <thead>
          <tr>
            <th>일자</th>
            {/* <th>라우팅</th> */}
            <th>설비</th>
            <th>로트명</th>
            <th>포장단위</th>
            <th>목표수량</th>
          </tr>
        </thead>
        <tbody>
          {workDataArray.map((_, index) => {
            const inputProps = form.getInputProps(
              `works.${index}.worksWorkIdDeleteRequest.scheduledAt`,
            );
            inputProps.value = new Date(inputProps.value);

            return (
              <tr key={index}>
                <td>
                  <BaseDateInput
                    popoverProps={{ withinPortal: true }}
                    icon={<IconCalendar size="1.2rem" />}
                    valueFormat="YYYY-MM-DD"
                    {...inputProps}
                  />
                </td>
                <td>
                  <EquipmentAutoComplete
                    width="384px"
                    placeholder="설비 선택"
                    {...form.getInputProps(`works.${index}.worksWorkIdDeleteRequest.equipmentCode`)}
                    onSelectedData={(data) => {
                      form.setFieldValue(`works.${index}.equipmentType`, data?.equipmentType);
                      if (data?.equipmentType !== "PACKING") {
                        form.setFieldValue(
                          `works.${index}.worksWorkIdDeleteRequest.kgPerCount`,
                          "",
                        );
                      }
                    }}
                  />
                </td>
                {isPlan && (
                  <td>
                    <TextInput
                      placeholder="로트명"
                      {...form.getInputProps(
                        `works.${index}.productionPlansProductionPlanIdDeleteRequest.presetLotName`,
                      )}
                      disabled={form.values.works?.[index]?.trackingStatus !== "WAITING"}
                    />
                  </td>
                )}
                <td>
                  <Input
                    disabled={form.values.works?.[index]?.equipmentType !== "PACKING"}
                    placeholder="포장단위 입력"
                    {...form.getInputProps(`works.${index}.worksWorkIdDeleteRequest.kgPerCount`)}
                  />
                </td>
                <td>
                  <TextInput
                    placeholder="목표수량"
                    type="number"
                    {...form.getInputProps(
                      `works.${index}.worksWorkIdDeleteRequest.targetQuantity`,
                    )}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
      <Flex justify="flex-end">
        <Button onClick={onSave}>일괄 수정</Button>
      </Flex>
    </>
  );
}
